// =============================================================================
//  Copyright : 2019 semantics GmbH
//  Date      : $Date: 2026-01-14 17:43:36 +0100 (Wed, 14 Jan 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 159808 $
//  Purpose   : tables for vl nodes
// =============================================================================
// ---Third-party imports
import React from 'react'
import {Dropdown, Label, Progress, Transition} from 'semantic-ui-react'
import clsx from 'clsx'
import CsvDownloader from 'react-csv-downloader'
import {saveAs} from 'file-saver'

// ---Framework imports
import {
  dict,
  size,
  countBy,
  uniq,
  isNil,
  isArray,
  isObject,
  isString,
  isDate,
  flatten,
  sorted,
  groupBy,
  mapItems,
  values,
} from 'ldash'
import {Actions, Sel} from 'uix/registries'
import {AutoPortal, PortalTarget, DebugFeature, XMenu, Toolbar} from 'uix/components'
import {VIcon} from 'uix/graphics'
import {
  useSelector,
  useSelectorShallow,
  useState,
  usePersistentShallow,
  useMemo,
  useGraphics,
  useEffect,
  useActions,
  useRouter,
  usePartContext,
  useInView,
} from 'uix/hooks'
import log from 'uix/logging'

import {hashObj} from 'uix/utils'
import {TypeChooser, useChosenTypes} from 'uix/types/components'
import {usePersistentTableStateRaw, SelectableTable} from 'uix/components/table'
import {formatDate} from 'uix/utils'
import {ChatPanel} from 'uix/components/llm/chat'

// ---Local imports
import styles from './styles.scss'
import {
  useTreeRowData,
  useCustomTreeList,
  indexRows,
  useTreeList,
  getFieldsForColid,
  useColumnsConfig,
  useTreelistContext,
} from './hooks'
import {
  configureColumns,
  getNoDataColumns,
  getHiddenColumns,
  ColumnsById,
  getColumnDescriptionText,
} from './columns'
import {
  ColumnContext,
  getColumLabel,
  getColumnNamespaceLabel,
  useColumnContext,
} from './columnTypes'
import {ColumnListerDialog} from './columnLister'
import {useQueryParamOverride, useQueryParamOverrideList} from './link_persist'
import {useLlmTableFunctionCallDecoding} from './llm'

import {ExpressionFilterItem, useCustomExpressionRegistration} from './expression_cols'

// ---Globals
const EMPTY = {}
const SIGMA = String.fromCodePoint(0x03a3)

const INITIAL_OPTIONS_VER = 8
const INITIAL_OPTIONS = {
  cols: [],
  groupBy: null,
  filterable: true,
  selectable: true,
  programmable: false,
  columnListOn: false,
  showFooter: false,
  query: null, // custom listid query (only lucene atm)
  groupedDateGranularity: 'd',
  pageSize: 50,
}
const getPersistsKey = storeKey => 'DBTableOpts' // storeKey ? `DBTableOpts_${storeKey}`: 'DBTableOpts'

const sortedCols = (unordered, ordered) => sorted(unordered, colid => ordered.indexOf(colid))

const usePersistentOptions = storeKey =>
  usePersistentShallow(storeKey || 'TreeListOptions', INITIAL_OPTIONS, INITIAL_OPTIONS_VER)

const getColumnHeaderText = ({colid, i18n, columnLabel, columnNamespaceLabel, sep = '/'}) => {
  /* generate translated string from namespace + column id
   */
  let col = ColumnsById[colid]
  let text = getColumLabel(colid, i18n, col?.options.i18nKey, columnLabel, false)
  if (colid.includes('.')) {
    let s = getColumnNamespaceLabel(colid.split('.')[0], i18n, columnNamespaceLabel, false)
    // for some states, the value label equals the namespace label like Nutzungsbedingungen/Nutzungsbedingungen
    // prevent this duplication
    if (s != text) {
      text = `${s}${sep}${text}`
    }
  }
  return text
}
const ColumnListerDropdown = ({
  columns,
  unlabeledSelection = false,
  storeKey,
  columnLabel: _columnLabel,
}) => {
  let i18n = useSelector(Sel.i18n)
  let domains = useSelector(Sel.domains)
  let {columnLabel, columnNamespaceLabel} = useColumnContext()
  columnLabel = _columnLabel || columnLabel
  // prefer translations from columnLabel func and fallback to column cfg
  let options = columns.map(colid => {
    let text = getColumnHeaderText({colid, i18n, columnLabel, columnNamespaceLabel})
    return {
      text,
      key: colid,
      value: colid,
    }
  })

  let [opts, setOpts] = usePersistentOptions(storeKey)
  // no multi-select labels
  let renderLabel = unlabeledSelection ? () => null : undefined

  return (
    <Dropdown
      title={i18n.get('table_choose_columns_title')}
      placeholder={i18n.get('table_choose_columns')}
      multiple
      search
      selection
      clearable
      scrolling
      compact
      direction="left"
      renderLabel={renderLabel}
      value={opts.cols}
      options={options}
      onChange={(e, data) => setOpts({cols: sortedCols(data.value, columns)})}
      text={`${opts.cols.length} gewählt`}
      style={{minWidth: 200}}
    />
  )
}
const GroupByChooser = ({groupableColumns, storeKey}) => {
  let i18n = useSelector(Sel.i18n)
  let [opts, setOpts] = usePersistentOptions(storeKey)
  let {groupByColumns, columnLabel, columnNamespaceLabel} = useColumnContext()

  let HARD = [{id: 'type', groupBySupport: true}, {id: 'domain', groupBySupport: true}]
  let cols = [...HARD, ...values(useColumnsConfig())]

  // remove "active" grouping cols from set
  let filter_colids = new Set(groupByColumns || [])

  let options = cols
    .filter(e => isNil(groupableColumns) || groupableColumns.includes(e.id)) // apply external filter
    .filter(e => e.groupBySupport && (isNil(groupByColumns) || filter_colids.has(e.id)))
    .map(e => {
      let text = getColumnHeaderText({colid: e.id, i18n, columnLabel, columnNamespaceLabel})
      return {
        text,
        value: e.id,
      }
    })
  return (
    <XMenu.Dropdown
      title={i18n.get('table_groupby_title')}
      placeholder={i18n.get('table_groupby')}
      search
      selection
      clearable
      value={opts.groupBy}
      options={options}
      onChange={(e, data) => setOpts({groupBy: data.value})}
      basic
      style={{minWidth: 200}}
    />
  )
}

const filterCfg = options => ({
  filterMethod: (filter, row, ...rest) => {
    if (filter.value === 'all') {
      return true
    } else if (row._aggregated) {
      return row[filter.id][0][filter.value] // check if in (nested) countBy result
    } else {
      return String(row[filter.id]) === filter.value
    }
  },
  Filter: ({filter, onChange}) => (
    <select
      onChange={event => onChange(event.target.value)}
      style={{width: '100%'}}
      value={filter ? filter.value : 'all'}
    >
      <option key="all" value="all">
        Alle
      </option>
      {options}
    </select>
  ),
})

const unknownColumn = (name, opts) => ({
  Header: name,
  accessor: () => `unknown column: ${name}`,
  aggregate: vals => '',
  id: name,
  ...(opts ?? {}),
})

const getColumns = ({
  rows,
  treeListOpts,
  domains,
  dname,
  i18n,
  buildLink,
  groupByValue,
  chosenColumns,
  unchosableColumns,
  columnNamespaceLabel,
  groupHeaders,
  showFooter,
  onUnSelect,
  groupedDateGranularity,
  setGroupedDateGranularity,
}) => {
  /*
    Generate a list of columns and their configuration for a dynamic data table.

    Args:
      - rows: (Array) The data to be displayed in the table.
      - treeListOpts: (Object) Configuration of the rows list {types, subdomains, ...}
      - domains: (Object) Domain information for the data.
      - i18n: (Object) Internationalization object for localizing labels.
      - buildLink: (Function) A function to build links for navigation.
      - groupByValue: (String) The value by which the rows are grouped.
      - chosenColumns: (Array) Columns selected to be shown in the table.
      - unchosableColumns: (Array) Columns that cannot be selected.
      - columnNamespaceLabel: (Function) A function to provide labels for column namespaces.
      - groupHeaders: (Boolean | String) Determines whether to group headers and its mode ('auto' or explicit boolean).
      - showFooter: (Boolean) Flag to show or hide the footer.
      - onUnSelect: (Function) A callback function when a column is unselected.
      - groupedDateGranularity: (String) The granularity of grouped dates.
      - setGroupedDateGranularity: (Function) A setter for the granularity of grouped dates.

    It uses memorization hooks to store index and column configurations. It filters out
    the domain column when it is not required. It can group columns by headers if needed.
    The footer can be toggled using the 'showFooter' argument. The returned object includes
    the configured columns, row index, and an array of column identifiers.

    Returns:
      - An object containing 'columns', 'index', and 'columnIds'.
  */
  let apiurl = useSelector(Sel.apiurl)
  let itypes = useSelector(root => root.config.instance.itypes)
  let user = useSelector(Sel.user)
  let activeDescendantTypes = useSelector(Sel.activeDescendantTypes)

  let index = useMemo(() => indexRows(rows, itypes), [rows, itypes])

  if (index.odids.length == 1 && groupByValue !== 'domain') {
    chosenColumns = chosenColumns.filter(name => name !== 'domain')
  }

  let column_env = {
    // shared with useColumnsConfig()
    dname,
    i18n,
    domains,
    itypes,
    activeDescendantTypes,
    user,
    apiurl,
    // not in useColumnsConfig()
    rowIndex: index,
    treeListOpts,
    buildLink,
    chosenColumns,
    unchosableColumns,
    onUnSelect,
    groupByValue,
    groupedDateGranularity,
    setGroupedDateGranularity,
  }

  // return stable reference with respect to chosenColumns
  let {cols, columnIds} = useMemo(() => {
    let columns = configureColumns(column_env)
    let columnsIdx = dict(columns.map(c => [c.id || c.accessor, c]))
    let cols = chosenColumns.map(name => columnsIdx[name] || unknownColumn(name))

    if (!showFooter) {
      // remove Footer from col cfg
      cols = cols.map(({Footer, ...col}) => col)
    }
    // if we wrap columns with group headers its not easy to get a list of active colids
    let columnIds = cols.map(c => c.id)

    let showGroupHeaders = !!groupHeaders
    if (groupHeaders == 'auto') {
      // if more than one namespace available
      showGroupHeaders = uniq(cols.map(col => col.namespace).filter(e => e)).length > 1
    }

    if (showGroupHeaders) {
      let byNamespace = groupBy(cols, col => (col.id.includes('.') ? col.id.split('.')[0] : ''))
      if (size(byNamespace) > 1) {
        cols = mapItems(byNamespace, (ns, cols) => ({
          Header: ns ? getColumnNamespaceLabel(ns, i18n, columnNamespaceLabel) : 'Allgemein',
          columns: cols,
        }))
      }
    }

    return {cols, columnIds}
  }, [
    hashObj(chosenColumns),
    index, // some column filter depend on row index values (like available types)
    groupHeaders,
    showFooter,
    groupByValue,
    groupedDateGranularity,
    //~ ...values(column_env),
  ])

  return {
    columns: cols,
    index,
    columnIds,
  }
}

export const buildCsvData = ({
  sortedData,
  internalColumns,
  i18n,
  columnLabel,
  columnNamespaceLabel,
  groupBy,
}) => {
  // flatten column grouping
  let flat_cols = flatten(internalColumns.map(c => (c.columns ? c.columns : [c])))
  let byColId = dict(flat_cols.map(c => [c.id, c]))
  let colids = flat_cols.map(c => c.id).filter(n => n != 'checkbox')
  if (groupBy) {
    colids = ['_count_', ...colids]
    // probably not useful - repeats type-grouping
    colids = colids.filter(colid => colid != 'tcaption')
  }
  let csv_cols = colids.map(colid => {
    let displayName = getColumnHeaderText({colid, i18n, columnLabel, columnNamespaceLabel})
    if (colid == '_count_') {
      displayName = 'Anzahl'
    }
    return {id: colid, displayName}
  })

  let get_value = (row, colid, target) => {
    let v = row[colid]
    //~ log.x({row, colid, target})
    if (colid == 'tcaption') {
      // not available during groupBy
      v = row._original?.caption || v
    } else if (colid == '_count_') {
      return size(row._subRows)
    }

    if (target == 'csv') {
      if (isArray(v)) {
        v = v[0]
      }

      let col_cfg = byColId[colid]
      if (col_cfg.serializeCSV) {
        v = col_cfg.serializeCSV(v, row)
      } else if (isObject(v)) {
        v = mapItems(v, (k, v) => `${k}: ${v}`).join(', ')
      } else if (isDate(v)) {
        v = formatDate(v, 'ISO_DATE')
      } else if (isString(v) && v.includes(';')) {
        v = v.replaceAll(';', '')
      }
    } else {
      // probably first element of a groupBy
      if (isArray(v)) {
        v = v[0]
      }
    }

    return v
  }

  return {
    datas: sortedData.map(r => dict(colids.map(cid => [cid, get_value(r, cid)]))),
    csv_datas: sortedData.map(r => dict(colids.map(cid => [cid, get_value(r, cid, 'csv')]))),
    csv_cols,
  }
}
const CsvItem = ({sortedData, internalColumns, groupBy}) => {
  let {columnLabel, columnNamespaceLabel} = useColumnContext()
  let i18n = useSelector(Sel.i18n)

  // calculate csv datas only if popped up
  let [ref, onScreen] = useInView({triggerOnce: false})
  let {csv_datas, csv_cols, datas} = useMemo(() => {
    if (!onScreen) return {}
    return buildCsvData({
      sortedData,
      internalColumns,
      i18n,
      columnLabel,
      columnNamespaceLabel,
      groupBy,
    })
  }, [onScreen])
  let build = ({text, ...props}) => (
    <CsvDownloader datas={csv_datas} filename="vl_liste" wrapColumnChar={'"'} {...props}>
      <a href="#">{text}</a>
    </CsvDownloader>
  )
  let save = () => {
    let blob = new Blob([JSON.stringify(datas)], {type: 'text/plain;charset=utf-8'})
    saveAs(blob, 'vl_liste.json')
  }
  return (
    <XMenu.PopupItem icon="download">
      <span ref={ref} />
      {build({text: 'Download CSV'})}
      {build({text: 'Download CSV (Excel)', separator: ';'})}
      {build({text: 'Download CSV (Excel, übersetzt)', separator: ';', columns: csv_cols})}
      <a href="#" onClick={save}>
        Download JSON
      </a>
    </XMenu.PopupItem>
  )
}

const TableChatDialog = ({storeKey}) => {
  let [opts, setOpts] = usePersistentOptions(storeKey)
  let [listOpts, setListOpts] = useTreelistContext()
  let [typeNames, setTypeNames] = useChosenTypes()
  // react table opts: sorted, filtered
  let [tableOpts, setTableOpts] = usePersistentTableStateRaw(getPersistsKey(storeKey))

  let colcfg = useColumnsConfig()
  let [functionCall, setFunctionCall] = useState(null)
  let {
    types,
    columns,
    groupBy,
    showFooter,
    filtered,
    tblsorted,
    showMaxRecords,
  } = useLlmTableFunctionCallDecoding({colcfg, functionCall})

  useEffect(() => {
    if (size(types)) setTypeNames(types)
  }, [types.join(',')])

  useEffect(() => {
    setOpts({
      cols: columns,
      groupBy,
      showFooter: !!showFooter,
      pageSize: showMaxRecords || INITIAL_OPTIONS.pageSize,
    })
  }, [columns.join(','), groupBy, showFooter])

  useEffect(() => {
    setTableOpts({filtered, sorted: tblsorted})
  }, [hashObj(filtered), hashObj(tblsorted)])

  let contextRules = useMemo(() => {
    let exclude = ['caption']
    return {
      table_func: {
        buildData: () => ({selected_columns: getColumnDescriptionText(colcfg, exclude)}),
        label: 'Table-Support',
        buildChatIndicator: () => null,
      },
    }
  }, [colcfg])

  return (
    <div style={{height: '100%', overflow: 'hidden'}}>
      <ChatPanel onFunctionCall={setFunctionCall} contextRules={contextRules} />
    </div>
  )
}
const TreeTableMenu = ({
  menu,
  rows,
  columns,
  columnChooseMode,
  selected,
  groupableColumns,
  options,
  storeKey,
  goToPersistentLink,
  sortedData,
  internalColumns,
  showChat,
  programmable,
}) => {
  /* rules: hard set options.* are not shown
    do not build <Menu> wrapper if menu.wrap == false
  */

  let menu_cfg = {wrap: true, items: []}
  if (isObject(menu)) {
    menu_cfg = {...menu_cfg, ...menu}
  } else if (isArray(menu)) {
    menu_cfg = {...menu_cfg, items: menu}
  }

  let i18n = useSelector(Sel.i18n)

  // some hard opts are never shown
  let is_hard_opt = n => {
    if (['filterable', 'selectable'].includes(n)) {
      return !isNil(options[n])
    }
    return false
  }

  let visible = dict(
    ['filterable', 'selectable', 'groupable', 'choosable'].map(n => {
      return [n, !is_hard_opt(n) && (menu === true || menu_cfg.items.includes(n))]
    })
  )

  let [opts, setOpts] = usePersistentOptions(storeKey)

  let items = (
    <Toolbar.Group title="Tabellen Eigenschaften">
      <XMenu.Item
        content={SIGMA}
        style={{fontWeight: 'bold'}}
        title="Footer"
        active={opts.showFooter}
        onClick={() => setOpts({showFooter: !opts.showFooter})}
      />
      {visible.filterable && (
        <XMenu.Item
          icon={['filter', 'table']}
          title={i18n.get('btn_toggle_filterable')}
          active={opts.filterable}
          onClick={() => setOpts({filterable: !opts.filterable})}
        />
      )}

      {programmable && <ExpressionFilterItem rows={rows} availableColumns={columns} />}

      {visible.groupable && (
        <GroupByChooser storeKey={storeKey} groupableColumns={groupableColumns} />
      )}
      {visible.choosable && columnChooseMode == 'panel' && (
        <XMenu.Item
          title={i18n.get('table_choose_columns_title')}
          active={opts.columnListOn}
          onClick={() => setOpts({columnListOn: !opts.columnListOn})}
        >
          <VIcon name="tbl_columns" />
          <Label
            className={clsx(styles.label, selected.length === 0 ? styles.hidden : null)}
            attached="bottom right"
            size="mini"
          >
            {selected.length}
          </Label>
        </XMenu.Item>
      )}
      <CsvItem sortedData={sortedData} internalColumns={internalColumns} groupBy={opts.groupBy} />
      <Toolbar.More>
        <DebugFeature>
          {visible.selectable && (
            <XMenu.Item
              icon="check square outline"
              title={i18n.get('btn_toggle_selectable')}
              active={opts.selectable}
              onClick={() => setOpts({selectable: !opts.selectable})}
            />
          )}
        </DebugFeature>
        {visible.choosable && columnChooseMode == 'dropdown' && (
          <ColumnListerDropdown columns={columns} storeKey={storeKey} unlabeledSelection />
        )}
        {goToPersistentLink && (
          <XMenu.Item
            icon={['world', {name: 'link', color: 'blue'}]}
            title="Persistent Url"
            onClick={goToPersistentLink}
          />
        )}
        {showChat && (
          <DebugFeature>
            <XMenu.DialogItem icon="chat" dlgStoreKey="llmDialog">
              <TableChatDialog storeKey={storeKey} />
            </XMenu.DialogItem>
          </DebugFeature>
        )}
      </Toolbar.More>
    </Toolbar.Group>
  )
  if (!menu_cfg.wrap) {
    return items
  }
  return items
}

const defaultFilterMethod = (filter, row) => {
  const id = filter.pivotId || filter.id
  return row[id] !== undefined
    ? String(row[filter.id])
        .toLowerCase()
        .startsWith(filter.value)
    : true
}

export const TreeTable = ({
  // list of vl nodes with shape [{otid, odid, datatype, subtype}] (output of useTreeList)
  // or dict {list, options} containing additional list control parameters (for persistence and column control)
  data = [],
  style, // css style pass through to SelectableTable
  showMenu = false, // either true or list of active menu item names (selectable, filterable, ....)
  menuPortal = 'TabMenuPortal',
  visibleMenu, // enable/disable menu items
  paginationPortal, // for beaming pagination into some header
  buildLink,
  defaultColumns, // external default column list (more cols are choosable)
  groupByColumns, // custom set (colids) of available groupby/pivot columns
  columns, // external chosable column list
  columnChooseMode = 'panel', // either "dropdown" or "panel"
  columnLabel, // custom label func for column headers
  columnNamespaceLabel, // custom label func for column namespaces
  groupHeaders = false, // if true, group column by namespace (secondary header); if "auto" show if more than one namespace + more than 5 cols
  showCount = true, // show count in pagination
  showPaginationTop = true,
  onSelectionChange, // track selection in outer comp
  selectionDep, // reset selection on change
  onColumnData, // use this for accessing data of fetched columns, maybe load more data
  sorted: tblsorted, // explicit sorting
  defaultSorted,
  showFooter, // show footer
  filtered,
  defaultFiltered,
  SubComponent, // function or react element for drawing an expandable subcomponent (expander will be added as last row)
  options = EMPTY, // hard overide manually persisted options (filterable, selectable)
  defaultPageSize,
  groupableColumns, // explicit list of groupable columns (groupable=true)
  tableKey, // reset component
  persist = true, // activating persistence will overwrite sorted/filtered internally (and default*)
  storeKey, // store key for persistence
  tableStateComponents, // a list of functions that render ({internalColumns, sortedData}) (probably via AutoPortal/PortalTarget)
  renderCustomComponent, // prevent rendering of default table
  showChat = false,
  programmable = false,
}) => {
  let copyIdsToClipboard = useActions('copyIdsToClipboard')

  let treeListOpts = {}
  let rows = []
  if (isObject(data)) {
    rows = data.list
    treeListOpts = data.options
  } else {
    rows = data
  }

  let {name: partName, i18n, dname} = usePartContext()
  let apiurl = useSelector(Sel.apiurl)
  let {loadedDomains, domains} = useSelectorShallow(root => ({
    loadedDomains: root.config.ddata,
    domains: root.config.domains,
  }))

  let [opts, setOpts] = usePersistentOptions(storeKey)
  opts = useMemo(() => ({...opts, ...options}), [opts, options])

  if (isNil(showFooter)) {
    showFooter = opts.showFooter
  }

  let pageSize = opts.pageSize || INITIAL_OPTIONS.pageSize
  useEffect(() => {
    if (defaultPageSize !== opts.pageSize) {
      setOpts({pageSize: defaultPageSize})
    }
  }, [defaultPageSize])

  let {cols, groupBy: groupByValue} = opts

  // start with persisted list
  let chosenColumns = [...cols]
  // filter down on external column list
  if (columns) {
    chosenColumns = chosenColumns.filter(c => columns.includes(c))
  }
  // prepend default columns
  if (!defaultColumns) {
    defaultColumns = []
    if (!chosenColumns.includes('tcaption') && !chosenColumns.includes('caption')) {
      if (!groupByValue) {
        defaultColumns.push('tcaption')
      }
    }
  }
  chosenColumns = [...defaultColumns, ...chosenColumns]
  // defaults are unchosable
  let unchosableColumns = defaultColumns

  // ensure stable empty list result (all filter are reapplied if table props change)
  let pivotBy = useMemo(() => (groupByValue ? [groupByValue] : []), [groupByValue])

  if (groupByValue) {
    // a groupByValue is always chosen
    chosenColumns = uniq([...chosenColumns, groupByValue])
    // a groupByValue is unchosable
    unchosableColumns = uniq([...unchosableColumns, groupByValue])
  }

  if (programmable) {
    chosenColumns = useCustomExpressionRegistration(chosenColumns)
  }

  // XXX: get rid of coltype.needsColData and getNoDataColumns
  // use: empty options.fields
  let colcfg = useColumnsConfig()
  let columnConfig = values(colcfg)
  let noDataColumns = getNoDataColumns(columnConfig)
  let hiddenColumns = getHiddenColumns(columnConfig)

  let loadableColumns = chosenColumns.filter(e => !noDataColumns.has(e))
  let loadableFields = flatten(loadableColumns.map(getFieldsForColid))
  if (SubComponent) {
    chosenColumns = [...chosenColumns, 'expander']
  }
  // do not request 'type' field
  //~ log.x(chosenColumns)
  let progressId = storeKey || partName
  let progress = useSelector(root => root.data.db.progressTreeFields[progressId] || EMPTY)
  let treeRowData = useTreeRowData(rows, loadableFields, progressId)
  //~ log.x(treeRowData)

  useEffect(() => {
    if (treeRowData && onColumnData) {
      onColumnData(treeRowData)
    }
  }, [treeRowData])

  if (treeRowData) {
    rows = treeRowData
  }

  let [selected, setSelected] = useState([])
  let maxRows = 100
  let columnContext = {columnLabel, columnNamespaceLabel, groupByColumns, hiddenColumns}
  let {columns: activeColumns, index: rowIndex, columnIds: activeColumnIds} = getColumns({
    rows,
    treeListOpts,
    domains,
    dname,
    i18n,
    buildLink,
    groupByValue,
    chosenColumns,
    unchosableColumns,
    columnNamespaceLabel,
    groupHeaders,
    showFooter,
    onUnSelect: colid => setOpts({cols: cols.filter(c => c !== colid)}),
    groupedDateGranularity: opts.groupedDateGranularity,
    setGroupedDateGranularity: v => setOpts({groupedDateGranularity: v}),
  })

  // on-mount: derive opts from query params
  let [columnFilter, setColumnFilter] = useState([])
  let persistKey = getPersistsKey(storeKey)
  let [rtOpts] = usePersistentTableStateRaw(persistKey)

  let {goToPersistentLink} = useQueryParamOverride(
    opts,
    setOpts,
    // data list level opts (subdomains, descendants, links, types)
    treeListOpts,
    // react table level (filtered, sorted)
    rtOpts,
    activeColumnIds
  )
  let selectionChange = selected => {
    if (onSelectionChange) {
      onSelectionChange(selected)
    }
    setSelected(selected)
  }

  let pageSizeOptions = useMemo(
    () =>
      sorted(
        uniq(
          [pageSize, 10, 50, 100, 250, 500, Math.min(rows.length, 1000)].filter(
            e => e <= rows.length || e == 50
          )
        )
      ),
    [rows, pageSize]
  )

  //~ pivotBy = ['type', ...pivotBy]
  //~ tableKey = `${tableKey}_${opts.groupedDateGranularity}`
  //~ log.x(tableKey)

  let renderHooked = ({sortedData, internalColumns, pivotBy}) => {
    let customRenderProps = {columns, sortedData, internalColumns, pivotBy}
    let tscomps = null
    if (tableStateComponents) {
      tscomps = tableStateComponents.map((func, i) => (
        <React.Fragment key={i}>{func(customRenderProps)}</React.Fragment>
      ))
    }
    return (
      <>
        {tscomps}
        {showMenu && (
          <AutoPortal target={menuPortal}>
            <TreeTableMenu
              rows={rows}
              columns={columns}
              columnChooseMode={columnChooseMode}
              columnLabel={columnLabel}
              groupableColumns={groupableColumns}
              selected={cols}
              options={options}
              menu={showMenu}
              storeKey={storeKey}
              goToPersistentLink={goToPersistentLink}
              internalColumns={internalColumns}
              sortedData={sortedData}
              showChat={showChat}
              programmable={programmable}
            />
          </AutoPortal>
        )}
        {renderCustomComponent ? renderCustomComponent(customRenderProps) : null}
      </>
    )
  }
  return (
    <ColumnContext.Provider value={columnContext}>
      <Transition visible={progress && progress.step > 0} animation="fade">
        <div style={{position: 'absolute', top: 0, left: 0, width: '100%'}}>
          <Progress size="tiny" indicating value={progress.step} total={progress.steps} />
        </div>
      </Transition>
      <SelectableTable
        key={tableKey}
        selectionKey="otid"
        selectionDep={selectionDep}
        style={style}
        className={clsx('dynHeight', '-striped', '-highlight', styles.treeTable)}
        data={rows}
        showPagination={true || pivotBy.length == 0}
        showPaginationTop={showPaginationTop}
        showPaginationBottom={false}
        columns={activeColumns}
        pageSize={pageSize}
        onPageSizeChange={(pageSize, pageIndex) => setOpts({pageSize})}
        minRows={0}
        pageSizeOptions={pageSizeOptions}
        filterable={opts.filterable}
        selectable={opts.selectable}
        defaultFilterMethod={defaultFilterMethod}
        sorted={tblsorted}
        defaultSorted={defaultSorted}
        filtered={filtered}
        defaultFiltered={defaultFiltered}
        pivotBy={pivotBy}
        onSelectionChange={selectionChange}
        onCopyToClipboard={selected => copyIdsToClipboard({otids: selected})}
        SubComponent={SubComponent}
        showCount={showCount}
        showFooter={showFooter}
        persist={persist}
        persistKey={persistKey}
        renderHooked={renderHooked}
        renderTable={!renderCustomComponent}
        paginationPortal={paginationPortal}
      />

      {opts.columnListOn && (
        <ColumnListerDialog
          onClose={() => setOpts({columnListOn: !opts.columnListOn})}
          selected={cols}
          unchosableColumns={unchosableColumns}
          chosableColumns={columns}
          onChange={columns => setOpts({cols: columns})}
          datatypes={rowIndex.datatypes}
        />
      )}
    </ColumnContext.Provider>
  )
}

let useGraphicsLoaded = () => {
  let graphics = useGraphics(['types', 'states'])
  return size(graphics.types) && size(graphics.states)
}

export const CustomListTable = ({name, query, limit, typeNames, ...props}) => {
  /* table with data fetched by use custom list name
  a name references a python provided "customTreeList"
  - like transcription_published, transcription_begun, lucene
   */

  let subdomains = true
  let params = limit ? {limit, subdomains} : {subdomains}
  if (query) {
    params = {...params, query}
  }
  if (typeNames) {
    let types = typeNames.join(',')
    params = {...params, typeName: types}
    props = {...props, treeListOpts: {types}}
  }
  let {treeList} = useCustomTreeList(name, params)
  let g_loaded = useGraphicsLoaded()

  // XXX: seems not to wait
  if (!treeList || !g_loaded) null

  return <TreeTable data={treeList} {...props} />
}

export const IdListTable = ({otids, ...props}) => {
  /* with explict otid list
   */
  let {treeList} = useTreeList('idList', {otids})
  let g_loaded = useGraphicsLoaded()
  if (!treeList || !g_loaded) return null
  return <TreeTable data={treeList} {...props} />
}

export const LuceneTable = ({query, limit = 10, storeKey = 'luceneTable', ...props}) => {
  let options = {selectable: false, filterable: false}
  return (
    <CustomListTable
      name={'lucene'}
      limit={limit}
      query={query}
      showMenu={false}
      storeKey={storeKey}
      options={options}
      {...props}
    />
  )
}

export const SimpleCustomListTable = ({
  name, // use this server side custom list (setupTreeListProviders)
  query, // hardlock query (only if name = lucene)
  columns = [],
  defaultColumns,
  limit, // limit number of fetches
  showMenu = false,
  customMenu, // custom externally managed menu items
  storeKey,
}) => {
  let [opts, setOpts] = usePersistentOptions(storeKey)
  let menuActive = Boolean(showMenu || customMenu)
  let options = {selectable: false}
  return (
    <>
      {menuActive && (
        <XMenu attached>
          {name == 'lucene' && !query && (
            <XMenu.Input
              label={{content: 'Query', color: 'blue'}}
              defaultValue={opts.query}
              fluid
              debounce="500"
              onValueChange={value => setOpts({query: value})}
            />
          )}
          <PortalTarget as="div" id="SimpleCustomListTableMenu" />
          {customMenu}
          <ColumnListerDropdown
            unlabeledSelection
            columns={columns}
            columnLabel={colid => colid.split('.').slice(-1)[0]}
            storeKey={storeKey}
          />
        </XMenu>
      )}

      <CustomListTable
        name={name}
        defaultColumns={defaultColumns}
        columns={opts.cols}
        limit={limit}
        query={query || opts.query}
        options={options}
        showMenu={showMenu}
        menuPortal="SimpleCustomListTableMenu"
        storeKey={storeKey}
      />
    </>
  )
}

export const useFilterableTreeList = ({
  name,
  node,
  datatypeName = null,
  isCustomList = false,
  customListParams = {},
  includeNode = false, // add node to list
  limit = 100000,
}) => {
  /* combine treeList and control menu buttons
  datatypeName: restrict type filter to this datatype
   */
  let i18n = useSelector(Sel.i18n)
  let {request} = useRouter()
  let [params, setParams] = usePersistentShallow(
    `${name}_opts`,
    {subdomains: false, links: false, descendants: true},
    2
  )
  let treeListOpts = useQueryParamOverrideList(params, setParams)

  let [typeNames] = useChosenTypes()
  let typeName = typeNames.join(',') || datatypeName || 'metadata'

  let resolveLinks = true
  let list_params = {
    typeName,
    links: params.links,
    limit: request.query.limit ? parseInt(request.query.limit) : limit,
  }
  treeListOpts = useMemo(() => ({...treeListOpts, types: typeName}), [treeListOpts, typeName])

  if (node?.datatype != 10) {
    list_params = {
      ...list_params,
      parentid: node.otid,
      descendants: params.descendants,
      includeParent: includeNode,
    }
  } else {
    // domain mode, if no node given or dt=10
    list_params = {...list_params, subdomains: params.subdomains}
  }
  if (isCustomList) {
    list_params = {...list_params, customListName: name, customListParams}
  }

  let listHash = hashObj(list_params)
  let {treeList, loading} = useTreeList(name, list_params, resolveLinks)

  let menuItems = (
    <>
      {node.datatype == 10 ? (
        <XMenu.Item
          icon="sub_domain"
          title={i18n.get('btn_include_childdomains')}
          active={params.subdomains}
          onClick={() => setParams({subdomains: !params.subdomains})}
        />
      ) : (
        <XMenu.Item
          icon="sitemap"
          title={i18n.get(`btn_include_children`)}
          active={params.descendants}
          onClick={() => setParams({descendants: !params.descendants})}
        />
      )}
      <XMenu.Item
        icon="chain"
        title={i18n.get(`btn_include_links`)}
        active={params.links}
        onClick={() => setParams({links: !params.links})}
      />
      <TypeChooser filteredDatatype={datatypeName} />
    </>
  )
  treeList = {list: treeList.list, count: treeList.count, options: treeListOpts}
  return {treeList, menuItems, loading, listHash}
}

export const TableCaseChooser = ({cases}) => {
  let [selected, setSelected] = useState(null)
  let {go} = useRouter()

  let setValue = name => {
    setSelected(name)
    let items = new URLSearchParams('?' + cases[name]).entries()
    let query = dict([...items])
    go({query})
  }
  let tblcases_opts = mapItems(cases, name => ({
    text: name,
    value: name,
    onClick: () => setValue(name),
  }))
  return <XMenu.Dropdown placeholder="Use-Case" value={selected} options={tblcases_opts} />
}

export const DataTable = ({
  types,
  subdomains = false,
  limit = null,
  persist = false,
  resolveLinks = false,
  ...props
}) => {
  let links = true
  let descendants = false

  let {treeList} = useTreeList(
    'datatable',
    {typeName: types, subdomains, links, descendants, limit},
    resolveLinks
  )
  let g_loaded = useGraphicsLoaded()

  // XXX: seems not to wait
  if (!treeList || !g_loaded) null
  return <TreeTable data={treeList} persist={persist} {...props} />
}
